import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type React from 'react';
import { useMemo, useState } from 'react';
import { withMicrosoftProvider } from '~/src/components/CalendarRecentPanel/helpers/calendarEventHelpers';
import { useCalendarTypes } from '~/src/hooks/useCalendarTypes';
import { calendarService } from '~/src/services/calendarService';
import chevronDownIcon from '~/static/assets/icons/landing-page/chevron-down-icon.svg';
import correctGreenIcon from '~/static/assets/icons/landing-page/correct-green.svg';
import watchLaterIcon from '~/static/assets/icons/landing-page/watch-later.svg';
import type { CalendarEvent, EventFilter } from '../../types';

type TodayEvents = {
  done: number;
  scheduled: number;
  ongoing: number;
};

const options: { label: string; value: EventFilter }[] = [
  { label: 'All events', value: 'all' },
  { label: "I'm the host", value: 'host' },
  { label: "I'm a participant", value: 'participant' },
];

interface EventTabProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onSearchInput: (value: string) => void;
  onSelectEventFilter: (value: EventFilter) => void;
}

type CalendarEventLike = CalendarEvent & {
  isCancelled?: boolean;
};

const EventTab: React.FC<EventTabProps> = ({
  activeTab,
  onTabChange,
  onSearchInput,
  onSelectEventFilter,
}) => {
  const { data: accountConnections, isLoading: isConnectionsLoading } = useCalendarTypes();

  const { data: googleEventsResponse, isLoading: isGoogleEventsLoading } = useQuery<
    CalendarEvent[]
  >({
    queryKey: ['eventTabGoogleEvents', accountConnections?.google?.connected],
    enabled: !!accountConnections?.google?.connected,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const google = accountConnections?.google;
      if (!google) {
        return [];
      }

      // Reuse list API but restrict server-side window to today only
      const page = await calendarService.fetchGoogleCalendarEventsList(
        google,
        activeTab,
        undefined,
        true,
      );
      const events = (page.events ?? []) as CalendarEvent[];

      return events.map((e) => ({
        ...e,
        provider: 'google',
      }));
    },
  });

  const { data: microsoftEventsResponse, isLoading: isMicrosoftEventsLoading } = useQuery<
    CalendarEvent[]
  >({
    queryKey: ['eventTabMicrosoftEvents', accountConnections?.microsoft?.connected],
    enabled: !!accountConnections?.microsoft?.connected,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const microsoft = accountConnections?.microsoft;
      if (!microsoft) {
        return [];
      }

      const page = await calendarService.fetchMicrosoftCalendarEventsList(
        microsoft,
        activeTab,
        undefined,
        true,
      );
      const events = (page.events ?? []) as CalendarEvent[];

      return events.map((e) => withMicrosoftProvider(e));
    },
  });

  const mergedAllEvents: CalendarEventLike[] = useMemo(() => {
    const google = (googleEventsResponse ?? []) as CalendarEventLike[];
    const microsoft = (microsoftEventsResponse ?? []) as CalendarEventLike[];
    return [...google, ...microsoft];
  }, [googleEventsResponse, microsoftEventsResponse]);

  const eventsStatus: TodayEvents = getTodayEventStats(mergedAllEvents);
  const isAnyProviderConnected =
    !!accountConnections?.google?.connected || !!accountConnections?.microsoft?.connected;
  const isEventStatsLoading =
    isConnectionsLoading ||
    !accountConnections ||
    (isAnyProviderConnected && (isGoogleEventsLoading || isMicrosoftEventsLoading));
  const doneCountLabel = String(eventsStatus?.done ?? 0);
  const scheduledCountLabel = String((eventsStatus?.scheduled ?? 0) + (eventsStatus?.ongoing ?? 0));
  const [selected, setSelected] = useState('all');
  const [isOpen, setIsOpen] = useState(false);

  const onSearch = (e: React.FormEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value;
    onSearchInput(value);
  };

  const handleSelect = (value: EventFilter) => {
    setSelected(value);
    setIsOpen(false);
    onSelectEventFilter(value);
  };

  function getTodayEventStats(events: CalendarEventLike[]) {
    const empty = { done: 0, scheduled: 0, ongoing: 0 };
    if (!Array.isArray(events)) {
      return empty;
    }

    const now = new Date();
    const dayStart = DateTime.now().startOf('day').toJSDate();
    const dayEnd = DateTime.now().endOf('day').toJSDate();

    let done = 0;
    let scheduled = 0;
    let ongoing = 0;

    events.forEach((event) => {
      if (event.isCancelled) {
        return;
      }

      if (!(event?.start && event?.end)) {
        return;
      }

      let start: Date;
      let end: Date;

      if (event.start.dateTime && event.end.dateTime) {
        start = DateTime.fromISO(event.start.dateTime, {
          zone: event.start.timeZone || 'utc',
        }).toJSDate();
        end = DateTime.fromISO(event.end.dateTime, {
          zone: event.start.timeZone || 'utc',
        }).toJSDate();
      } else if (event.start.date && event.end.date) {
        start = DateTime.fromISO(event.start.date).startOf('day').toJSDate();
        end = DateTime.fromISO(event.end.date).endOf('day').toJSDate();
      } else {
        return;
      }

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return;
      }

      if (end <= dayStart || start >= dayEnd) {
        return;
      }

      if (start <= now && now < end) {
        ongoing++;
      } else if (start > now) {
        scheduled++;
      } else {
        done++;
      }
    });

    return { done, scheduled, ongoing };
  }

  return (
    <div className="authenticated-event-tab-section">
      <div className="row-flex">
        <div className="events-detail">
          <h2>Events</h2>
          <div className="event-summary">
            <span className="done">
              <img
                alt=""
                aria-hidden="true"
                src={correctGreenIcon.src}
                width={correctGreenIcon.width}
                height={correctGreenIcon.height}
              />
              <h6>Today {isEventStatsLoading ? '0' : doneCountLabel} events done</h6>
            </span>
            <span className="seprateLine">|</span>
            <span className="scheduled">
              <img
                alt=""
                aria-hidden="true"
                src={watchLaterIcon.src}
                width={watchLaterIcon.width}
                height={watchLaterIcon.height}
              />
              <h6>{isEventStatsLoading ? '0' : scheduledCountLabel} scheduled</h6>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6.47505 5.23668L8.90005 7.50001L6.47505 9.76334C6.2313 9.99084 6.2313 10.3583 6.47505 10.5858C6.7188 10.8133 7.11255 10.8133 7.3563 10.5858L10.225 7.90834C10.4688 7.68084 10.4688 7.31334 10.225 7.08584L7.3563 4.40835C7.11255 4.18085 6.7188 4.18085 6.47505 4.40835C6.23755 4.63585 6.2313 5.00918 6.47505 5.23668Z"
                  fill="#18181B"
                />
              </svg>
            </span>
          </div>
        </div>

        <div className="tabs">
          <button
            type="button"
            className={activeTab === 'upcoming' ? 'tab active' : 'tab'}
            onClick={() => onTabChange('upcoming')}
          >
            Upcoming
          </button>
          <button
            type="button"
            className={activeTab === 'past' ? 'tab active' : 'tab'}
            onClick={() => onTabChange('past')}
          >
            Past events
          </button>
        </div>

        <div className="filter-search">
          <div className="dropdown">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.16667 15.5H10.8333C11.2917 15.5 11.6667 15.125 11.6667 14.6667C11.6667 14.2083 11.2917 13.8333 10.8333 13.8333H9.16667C8.70833 13.8333 8.33333 14.2083 8.33333 14.6667C8.33333 15.125 8.70833 15.5 9.16667 15.5ZM2.5 6.33333C2.5 6.79167 2.875 7.16667 3.33333 7.16667H16.6667C17.125 7.16667 17.5 6.79167 17.5 6.33333C17.5 5.875 17.125 5.5 16.6667 5.5H3.33333C2.875 5.5 2.5 5.875 2.5 6.33333ZM5.83333 11.3333H14.1667C14.625 11.3333 15 10.9583 15 10.5C15 10.0417 14.625 9.66667 14.1667 9.66667H5.83333C5.375 9.66667 5 10.0417 5 10.5C5 10.9583 5.375 11.3333 5.83333 11.3333Z"
                fill="#4C5863"
              />
            </svg>
            <div className="custom-select">
              <button
                type="button"
                className="custom-select-display"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                {options.find((o) => o.value === selected)?.label}
                <img
                  src={chevronDownIcon.src}
                  width={chevronDownIcon.width}
                  height={chevronDownIcon.height}
                  alt=""
                  className={`arrow ${isOpen ? 'open' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {isOpen && (
                <div className="custom-select-options">
                  {options.map((opt) => (
                    <label key={opt.value} className="custom-option">
                      <input
                        type="radio"
                        name="eventFilter"
                        value={opt.value}
                        checked={selected === opt.value}
                        onChange={() => handleSelect(opt.value)}
                      />
                      <span className="custom-radio" />
                      {opt.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="search-box">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 4.50003C6.93913 4.50003 5.92172 4.92146 5.17157 5.67161C4.42143 6.42175 4 7.43917 4 8.50003C4 9.5609 4.42143 10.5783 5.17157 11.3285C5.92172 12.0786 6.93913 12.5 8 12.5C9.06087 12.5 10.0783 12.0786 10.8284 11.3285C11.5786 10.5783 12 9.5609 12 8.50003C12 7.43917 11.5786 6.42175 10.8284 5.67161C10.0783 4.92146 9.06087 4.50003 8 4.50003ZM2 8.50003C1.99988 7.55574 2.22264 6.62475 2.65017 5.78278C3.0777 4.9408 3.69792 4.21163 4.4604 3.65456C5.22287 3.09749 6.10606 2.72825 7.03815 2.57687C7.97023 2.42549 8.92488 2.49625 9.82446 2.78338C10.724 3.07052 11.5432 3.56594 12.2152 4.22933C12.8872 4.89272 13.3931 5.70537 13.6919 6.60117C13.9906 7.49697 14.0737 8.45063 13.9343 9.38459C13.795 10.3185 13.4372 11.2064 12.89 11.976L17.707 16.793C17.8892 16.9816 17.99 17.2342 17.9877 17.4964C17.9854 17.7586 17.8802 18.0094 17.6948 18.1949C17.5094 18.3803 17.2586 18.4854 16.9964 18.4877C16.7342 18.49 16.4816 18.3892 16.293 18.207L11.477 13.391C10.5794 14.0293 9.52335 14.4082 8.42468 14.4862C7.326 14.5641 6.22707 14.3381 5.2483 13.833C4.26953 13.3279 3.44869 12.5631 2.87572 11.6224C2.30276 10.6817 1.99979 9.60147 2 8.50003Z"
                fill="#4C5863"
              />
            </svg>
            <input type="text" onInput={onSearch} placeholder="Search" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventTab;
